import { FC, useMemo } from "react";
// Redux
import { useAppSelector } from "hooks/useStore";
import { selectPreviewPageMode, selectResult } from "store/player/playerSelectors";
import { selectQuiz } from "store/quiz/quizSelectors";
import IQuestion from "models/Question";
import IAnswer from "models/Answer";
import QuizTypes from "types/QuizTypes";
// Components
import AnswerItem from "./AnswerItem";

type Props = {
  question: IQuestion;
  showQuestion: boolean;
}

const QuestionItem:FC<Props> = ({ question, showQuestion }) => {
  const result = useAppSelector(selectResult);
  const quiz = useAppSelector(selectQuiz);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

   const answers:IAnswer[] = useMemo(() => {
    if (!quiz) return [];
    if (quiz.type === QuizTypes.RankingPoll) {
      return quiz.questionsSettings.answersOptions || [];
    } else {
      return question.answers || [];
    }
  }, [quiz, question]);

  const results = useMemo(() => {
    if (previewPageMode) {
      return answers.map(() => ({
        questionRef: question.ref,
        answers: answers.reduce((acc, answer) => {
          acc[answer.ref] = Math.floor(Math.random() * 10);
          return acc;
        }, {} as Record<string, number>),
      }));
    }
    return result?.results || [];
  }, [previewPageMode, question, result, answers]);

  const questionResults = results.find((r:any) => r.questionRef === question.ref);

  if (!questionResults) return null;
  
  const questionAnswers = [...answers].sort((a, b) => {
    const aCount = questionResults.answers[a.ref] || 0;
    const bCount = questionResults.answers[b.ref] || 0;
    return bCount - aCount;
  });

  const allAnswers:number[] = [];

  Object.keys(questionResults.answers || {}).forEach((key) => {
    if (answers.find(answer => answer.ref === key)) {
      allAnswers.push(questionResults.answers[key] || 0)
    }
  })

  const filling = 100 / Math.max(...allAnswers);
  const sum = allAnswers.reduce((a,b) => a+b, 0);

  return (
    <div
      key={question.ref}
      className={`${showQuestion ? 'shadow-neu-content-inner' : ''} rounded-lg p-2 mb-2`}
    >
      {showQuestion ? <p className="font-bold text-center text-primary">{question.title}</p> : null}
      {questionAnswers.map(answer => {
        const value = questionResults.answers[answer.ref] || 0;
        const width = value * filling;
        const percent = Number((value / sum * 100).toFixed(2)) || 0;
        const userAnswer = questionResults.userAnswer === answer.ref;
        const label = answer.title;

        return <AnswerItem key={answer.ref} value={value} width={width} percent={percent} label={label} userAnswer={userAnswer} />
      })}
    </div>
  );
}
 
export default QuestionItem;
